import React, { useMemo, useRef } from "react";
import { Calendar, Clock, CaretLeft, CaretRight, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const DEFAULT_TIME_SLOTS = ["10:00", "11:30", "14:00", "15:30", "17:00"];
const DEFAULT_DAYS_AHEAD = 14;

/** Step 1 of the consult flow, horizontal date scroller + time-slot grid. */
export default function PickWhenStep({ settings, form, setForm, onNext, onCancel, canGoNext }) {
  const dateScrollerRef = useRef(null);

  const timeSlots = useMemo(() => {
    const raw = settings?.consultation_time_slots || DEFAULT_TIME_SLOTS.join(",");
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }, [settings]);

  const days = useMemo(() => {
    const n = Number(settings?.consultation_days_ahead) || DEFAULT_DAYS_AHEAD;
    return Array.from({ length: n }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() + i + 1);
      return d;
    });
  }, [settings]);

  const scrollDates = (dir) => {
    if (!dateScrollerRef.current) return;
    dateScrollerRef.current.scrollBy({ left: dir * 240, behavior: "smooth" });
  };

  return (
    <div className="md-consult-step" data-testid="consult-step-when-body">
      <div>
        <label className="md-label-eyebrow block mb-2"><Calendar size={12} className="inline mr-1" />Pick a date</label>
        <div className="consult-date-scroll-wrap">
          <button type="button" onClick={() => scrollDates(-1)} className="consult-date-arrow consult-date-arrow-l" aria-label="Scroll left" data-testid="consult-date-prev"><CaretLeft size={14} weight="bold" /></button>
          <div ref={dateScrollerRef} className="consult-date-scroller no-scrollbar">
            {days.map((d) => {
              const ds = d.toISOString().slice(0, 10);
              const active = form.date === ds;
              return (
                <button type="button" key={ds} onClick={() => setForm({ ...form, date: ds })}
                  className={`md-consult-chip ${active ? "active" : ""}`}
                  data-testid={`consult-date-${ds}`}>
                  <div className="text-[10px] opacity-70 uppercase tracking-wider">{d.toLocaleDateString("en-US", { weekday: "short" })}</div>
                  <div className="text-base font-serif">{d.getDate()}</div>
                  <div className="text-[10px] opacity-70">{d.toLocaleDateString("en-US", { month: "short" })}</div>
                </button>
              );
            })}
          </div>
          <button type="button" onClick={() => scrollDates(1)} className="consult-date-arrow consult-date-arrow-r" aria-label="Scroll right" data-testid="consult-date-next"><CaretRight size={14} weight="bold" /></button>
        </div>
      </div>

      <div className="mt-5">
        <label className="md-label-eyebrow block mb-2"><Clock size={12} className="inline mr-1" />Pick a time</label>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {timeSlots.map((t) => {
            const active = form.time_slot === t;
            return (
              <button type="button" key={t}
                onClick={() => {
                  if (!form.date) { toast.error("Please pick a date first"); return; }
                  setForm({ ...form, time_slot: t });
                }}
                className={`md-consult-time ${active ? "active" : ""}`}
                style={!form.date ? { opacity: 0.45, cursor: "not-allowed" } : undefined}
                data-testid={`consult-time-${t}`}>{t}</button>
            );
          })}
        </div>
        {!form.date && (
          <p className="text-xs mt-2" style={{ color: "var(--md-text-muted)" }} data-testid="consult-time-hint">
            Pick a date first to choose a time.
          </p>
        )}
      </div>

      <div className="consult-step-nav mt-7">
        <button type="button" onClick={onCancel} className="btn-outline text-sm" data-testid="consult-cancel">Cancel</button>
        <button type="button" onClick={onNext} className="btn-primary text-sm" style={!canGoNext ? { opacity: 0.6 } : undefined} data-testid="consult-next">
          Continue <ArrowRight size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
